// Link only — the job was already keyed into Foundation by accounting. Pick it from the live Foundation job list;
// the WO is marked synced WITHOUT pushing anything (Foundation keeps its start date / contract).
import { useEffect, useState } from "react";
import { Link2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { linkFoundationWoQueue, searchFoundationJobs } from "@/lib/api";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

export function FoundationLinkJobDialog({ row, onClose, onLinked }) {
  const [q, setQ] = useState(row.wo_number || "");
  const [res, setRes] = useState(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(row.in_foundation || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try { const { data } = await searchFoundationJobs(needle); setRes(data); setError(null); }
      catch (e) { setRes({ jobs: [] }); setError(e?.response?.data?.detail || "Could not read Foundation jobs"); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const inactive = picked && picked.record_status && picked.record_status.toUpperCase() !== "A";
  const [allowInactive, setAllowInactive] = useState(false);
  const link = async () => {
    if (!picked) return;
    setBusy(true); setError(null);
    try {
      const { data } = await linkFoundationWoQueue(row.id, picked.job_no, inactive && allowInactive);
      toast.success(data?.message || `Linked to Foundation job ${picked.job_no}`, { duration: 8000 });
      onLinked?.(data);
      onClose();
    } catch (e) { setError(e?.response?.data?.detail || "Could not link — nothing was changed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid={`foundation-link-dialog-${row.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-1.5"><Link2 className="w-4 h-4 text-violet-600" /> Link {row.wo_number} to an existing Foundation job</DialogTitle>
          <DialogDescription className="text-xs">
            Nothing is pushed. Foundation keeps the job's own start date, contract and estimated cost; ServiceOps just remembers the job number
            so change orders, purchase orders and actuals line up with it.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); setAllowInactive(false); }} placeholder="Foundation job # or description (2+ characters)" autoFocus
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900" data-testid={`foundation-link-search-${row.id}`} />
          {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 absolute right-2 top-1/2 -translate-y-1/2" />}
        </div>
        <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-800" data-testid={`foundation-link-results-${row.id}`}>
          {!res && <div className="text-[11px] text-slate-400 p-3 text-center">Type at least 2 characters.</div>}
          {res && res.jobs?.length === 0 && <div className="text-[11px] text-slate-400 p-3 text-center" data-testid={`foundation-link-empty-${row.id}`}>No Foundation job matches "{res.q || q}".</div>}
          {(res?.jobs || []).map((j) => {
            const sel = picked?.job_no === j.job_no;
            return (
              <button type="button" key={j.job_no} onClick={() => setPicked(j)}
                className={`w-full text-left px-2.5 py-1.5 text-xs flex items-start gap-2 ${sel ? "bg-violet-50 dark:bg-violet-950/40" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                data-testid={`foundation-link-job-${row.id}-${j.job_no}`}>
                <span className="font-mono font-semibold text-slate-800 dark:text-slate-100 w-24 shrink-0">{j.job_no}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{j.description || <span className="text-slate-400">no description</span>}</span>
                  <span className="block text-[10px] text-slate-500">
                    cust {j.customer_no || "—"} · start {j.job_start_date || "—"} · contract {money(j.original_contract)}
                    {j.completion_date ? ` · completed ${j.completion_date}` : ""}{j.record_status && j.record_status !== "A" ? ` · status ${j.record_status}` : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {picked && (
          <div className="text-[11px] rounded border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800 p-2" data-testid={`foundation-link-picked-${row.id}`}>
            Link <span className="font-mono font-semibold">{row.wo_number}</span> → Foundation job <span className="font-mono font-semibold">{picked.job_no}</span>
            {picked.description ? ` (${picked.description})` : ""}. Foundation keeps start {picked.job_start_date || "—"} and contract {money(picked.original_contract)}.
            {picked.job_no !== String(row.wo_number || "").trim() && <span className="block mt-0.5 text-amber-800">Job number differs from the WO number — make sure this is the right job.</span>}
            {inactive && (
              <label className="flex items-start gap-2 mt-1.5 text-rose-800 cursor-pointer" data-testid={`foundation-link-inactive-${row.id}`}>
                <input type="checkbox" checked={allowInactive} onChange={(e) => setAllowInactive(e.target.checked)} className="mt-0.5" data-testid={`foundation-link-inactive-confirm-${row.id}`} />
                <span>This job is <b>not active</b> in Foundation (record status {picked.record_status}). Link to it anyway.</span>
              </label>
            )}
          </div>
        )}
        {error && <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-2 whitespace-pre-wrap" data-testid={`foundation-link-error-${row.id}`}>{error}</div>}
        <div className="flex items-center gap-2">
          <button type="button" onClick={link} disabled={!picked || busy || (inactive && !allowInactive)}
            className="text-xs px-2.5 py-1 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`foundation-link-run-${row.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Link only — push nothing
          </button>
          <button type="button" onClick={onClose} disabled={busy} className="ml-auto text-xs px-2.5 py-1 rounded border border-slate-300 dark:border-slate-600" data-testid={`foundation-link-cancel-${row.id}`}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FoundationLinkJobDialog;
